import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mainBrand } from "../../constants/data";
import { phoneIcon } from "../../constants/svgIcons";
import '../../assets/css/home-slider.css';

// Import images statically to avoid runtime errors
import sliderItSolutions from '../../assets/images/sliders/slider-it-solutions.avif';
import iVisita from '../../assets/images/sliders/iVisita.avif';
import customSoftware from '../../assets/images/sliders/custom-software.avif';
import meetingRoom from '../../assets/images/sliders/meeting-room.avif';
import cFlow from '../../assets/images/sliders/c-flow.avif';
import paperlessBoardMeeting from '../../assets/images/sliders/paperless-board-meeting.avif';

const HomeSlider = () => {
  const textRef = useRef(null);
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentCaptionIndex, setCurrentCaptionIndex] = useState(0);
  const [slideTransition, setSlideTransition] = useState(true);
  const sliderRef = useRef(null);
  const intervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Map of imported images
  const imageImports = {
    "slider-it-solutions.avif": sliderItSolutions,
    "iVisita.avif": iVisita,
    "custom-software.avif": customSoftware,
    "meeting-room.avif": meetingRoom,
    "c-flow.avif": cFlow,
    "paperless-board-meeting.avif": paperlessBoardMeeting
  };

  const homeSlider = [
    {
      id: 1,
      title: 'IT Solutions',
      img: "slider-it-solutions.avif",
      routePath: 'products/visitor-management-software'
    },
    {
      id: 2,
      title: 'Visitor Management System',
      img: "iVisita.avif",
      routePath: 'products/visitor-management-software'
    },
    {
      id: 3,
      title: 'Custom Software',
      img: "custom-software.avif",
      routePath: 'services/customized-software'
    },
    {
      id: 4,
      title: 'Meeting Room',
      img: "meeting-room.avif",
      routePath: 'products/meeting-room-solutions'
    },
    {
      id: 5,
      title: 'C Flow ,Work Flow Software',
      img: "c-flow.avif",
      routePath: 'products/work-flow'
    },
    {
      id: 6,
      title: 'Paperless Board Meeting',
      img: "paperless-board-meeting.avif",
      routePath: 'products/paperless-board-meeting'
    },
  ];

  const captions = [
    "Transform <span class='text-theme-color'> Your Ideas </span> Into Reality With Our Expert IT Solutions",
    "Innovative <span class='text-theme-color'>Visitor Management System</span> for Modern Businesses",
    "We Make <span class='text-theme-color'>Custom Software</span> Based On Your Business Requirement Needs",
    "Smart <span class='text-theme-color'>Meeting Room Management</span> to Enhance Your Productivity",
    "Automate Workflows <span class='text-theme-color'>10x faster,</span> No Coding Needed !",
    "Seamless <span class='text-theme-color'>Digital Board Meetings</span> Fast and Sustainable Collaboration All In One",
  ];

  const startTypingAnimation = (text, callback) => {
    if (!textRef.current) return;

    textRef.current.innerHTML = '';
    clearTimeout(typingTimeoutRef.current);

    let charIndex = 0;
    let isTag = false;
    let currentTag = '';
    let visibleText = '';

    const typeNextChar = () => {
      if (charIndex >= text.length) {
        callback?.();
        return;
      }

      const currentChar = text[charIndex];

      if (currentChar === '<') {
        isTag = true;
        currentTag += currentChar;
        charIndex++;
        typingTimeoutRef.current = setTimeout(typeNextChar, 0);
        return;
      }

      if (isTag) {
        currentTag += currentChar;
        if (currentChar === '>') {
          isTag = false;
          visibleText += currentTag;
          currentTag = '';
        }
        charIndex++;
        typingTimeoutRef.current = setTimeout(typeNextChar, 0);
        return;
      }

      visibleText += currentChar;
      textRef.current.innerHTML = visibleText + (isTag ? currentTag : '');
      charIndex++;

      typingTimeoutRef.current = setTimeout(typeNextChar, Math.random() * 50 + 50);
    };

    typeNextChar();
  };

  const goToSlide = (index) => {
    setSlideTransition(false);
    setTimeout(() => {
      const safeIndex = Math.max(0, Math.min(index, homeSlider.length - 1));
      setCurrentSlide(safeIndex);
      setCurrentCaptionIndex(safeIndex);
      setSlideTransition(true);
      resetInterval();
    }, 10);
  };

  const goToNextSlide = () => {
    const nextIndex = (currentSlide + 1) % homeSlider.length;
    goToSlide(nextIndex);
  };

  const goToPrevSlide = () => {
    const prevIndex = (currentSlide - 1 + homeSlider.length) % homeSlider.length;
    goToSlide(prevIndex);
  };

  const resetInterval = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(goToNextSlide, 7000);
  };

  useEffect(() => {
    startTypingAnimation(captions[currentCaptionIndex]);
    resetInterval();

    return () => {
      clearTimeout(typingTimeoutRef.current);
      clearInterval(intervalRef.current);
    };
  }, [currentSlide]);

  useEffect(() => {
    startTypingAnimation(captions[currentCaptionIndex]);
  }, [currentCaptionIndex]);

  if (!homeSlider || homeSlider.length === 0) {
    return <div className="text-center py-20">No slides available</div>;
  }

  const currentSlideData = homeSlider[currentSlide] || {};
  const currentImage = imageImports[currentSlideData.img];

  return (
    <div>
      <div className="md:w-theme-container w-mobile-container mx-auto flex flex-col justify-between items-start gap-y-10">
        <div className="flex items-center w-full h-full overflow-hidden">
          <div className="w-full md:w-[50%] mt-32 md:mt-12 lg:mt-14 h-auto">
            <div className="w-full bg-amber-7000">
              <div
                className="lg:mt-16 md:mt-16 xl:leading-[3rem] lg:text-4xl md:text-3xl text-2xl h-20"
                ref={textRef}
              />
              <div className="md:mt-32 mt-20 md:leading-relaxed lg:text-xl md:text-lg text-base">
                {t('common.aboutUsDescription')}
              </div>
              <div className="">
                <a
                  href={`tel:+971 4 2829 613`}
                  className="w-[130px] py-3 text-base mt-8 h-10 rounded-full bg-theme-color text-white text-center flex items-center justify-center gap-x-1"
                >
                  {phoneIcon('#ffffff')} {t('common.callNow')}
                </a>
              </div>
            </div>
          </div>
          <div
            className="w-full md:w-[50%] md:flex justify-center items-center hidden relative"
            ref={sliderRef}
          >
            <div className="w-[90%] flex flex-col md:h-[460px] lg:h-[620px] items-end justify-between mt-12">
              <div className="w-full rounded overflow-hidden relative">
                <img
                  src={currentImage}
                  alt={currentSlideData.title || 'Slide image'}
                  className={`w-full ${slideTransition ? 'opacity-100 transition-opacity duration-500 ease-in-out' : 'opacity-0'}`}
                  // onError={(e) => {
                  //   e.target.src = fallbackImage;
                  // }}
                />
              </div>
              <div className="w-full h-9 bg-green-3000 flex items-center justify-center gap-x-4">
                {homeSlider.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`${currentSlide === index ? 'w-7' : 'w-3'}
                     h-3 rounded-full transition-all 
                     ${currentSlide === index ? 'bg-blue-500' : 'bg-gray-300'}`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div
          className="w-full md:w-[95%] lg:p-10 p-8 custom-radius mx-auto grid md:grid-cols-6 grid-cols-3 gap-x-10 overflow-hidden"
          style={{ borderRadius: '100px 100px 0 0' }}
        >
          {mainBrand.map((item, index) => (
            <div
              key={item.id}
              className="md:w-[80%] mx-auto animate-infinitePopPulse"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <img
                src={require(`../../assets/images/main-brand/${item.imgName}`)}
                alt={item.id}
                className="w-full h-auto"
                // onError={(e) => {
                //   e.target.src = fallbackImage;
                // }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomeSlider;